import React, { createContext, useContext, useState, useEffect } from "react";
import AuthService from "../services/authentication";

const TransitionGuideContext = createContext();

export const useTransitionGuide = () => {
  const context = useContext(TransitionGuideContext);
  if (!context) {
    throw new Error(
      "useTransitionGuide must be used within a TransitionGuideProvider"
    );
  }
  return context;
};

const STORAGE_KEY = "transition_guide_state";
const GUIDE_VERSION = "1.0";

// Define the transition guide steps
const TRANSITION_STEPS = [
  {
    id: "welcome",
    title: "Welcome to the new platform!",
    description:
      "Your patients and prescriptions have been migrated. Let's get you set up.",
    type: "info",
    completed: false,
  },
  {
    id: "pricing_setup",
    title: "Set Default Pricing",
    description: "Configure your markup and RRP preferences.",
    type: "action",
    completed: false,
  },
  {
    id: "patient_review",
    title: "Add Patient Emails",
    description: "Update patient contact information for prescription access.",
    type: "action",
    completed: false,
  },
  {
    id: "first_prescription",
    title: "Send First Prescription",
    description:
      "Review prescription details and send your first prescription to a patient.",
    type: "action",
    completed: false,
  },
  {
    id: "complete",
    title: "Setup Complete!",
    description: "You're ready to start using your new platform.",
    type: "complete",
    completed: false,
  },
];

export const TransitionGuideProvider = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState(TRANSITION_STEPS);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  const auth = AuthService.useAuth();

  // Load state from localStorage on mount and when auth changes
  useEffect(() => {
    const loadStoredState = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);

          // Check if this is the same version
          if (parsed.version === GUIDE_VERSION) {
            setSteps(parsed.steps || TRANSITION_STEPS);
            setCurrentStep(parsed.currentStep || 0);
            setIsMinimized(parsed.isMinimized || false);
            setIsDismissed(parsed.dismissed || false);

            // Show guide if not all steps are completed and user hasn't dismissed
            const allCompleted = (parsed.steps || TRANSITION_STEPS).every(
              (step) => step.completed
            );
            const shouldShow =
              !allCompleted &&
              !parsed.dismissed &&
              auth.authStatus === "authenticated" &&
              auth.user?.user?.pop_au_legacy_id;
            console.log("Guide visibility decision:", {
              allCompleted,
              dismissed: parsed.dismissed,
              authStatus: auth.authStatus,
              hasLegacyId: auth.user?.user?.pop_au_legacy_id,
              shouldShow,
            });
            setIsVisible(shouldShow);
          } else {
            // New version, reset state
            resetGuide();
          }
        } else {
          // First time, show the guide if user is authenticated and has pop_au_legacy_id
          setIsVisible(
            auth.authStatus === "authenticated" &&
              auth.user?.user?.pop_au_legacy_id
          );
        }
      } catch (error) {
        console.error("Error loading transition guide state:", error);
        resetGuide();
      } finally {
        setIsLoading(false);
      }
    };

    if (auth.authStatus === "authenticated") {
      // Only load for users with pop_au_legacy_id
      if (auth.user?.user?.pop_au_legacy_id) {
        loadStoredState();
      } else {
        setIsLoading(false);
        setIsVisible(false);
      }
    } else if (auth.authStatus === "unauthenticated") {
      setIsLoading(false);
      setIsVisible(false);
    }
    // If auth status is still loading, don't do anything yet
  }, [auth.authStatus]);

  // Also load on component mount to ensure state is restored immediately
  useEffect(() => {
    // Only load if not already loading due to auth effect
    if (
      auth.authStatus === "authenticated" &&
      auth.user?.user?.pop_au_legacy_id
    ) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.version === GUIDE_VERSION) {
            setSteps(parsed.steps || TRANSITION_STEPS);
            setCurrentStep(parsed.currentStep || 0);
            setIsMinimized(parsed.isMinimized || false);
            setIsDismissed(parsed.dismissed || false);

            const allCompleted = (parsed.steps || TRANSITION_STEPS).every(
              (step) => step.completed
            );
            setIsVisible(!allCompleted && !parsed.dismissed);
          }
        }
      } catch (error) {
        console.error("Error loading transition guide state on mount:", error);
      }
    }
  }, []); // Empty dependency array to run only on mount

  // Auto-complete the completion step when all action steps are done and we reach it
  useEffect(() => {
    const actionSteps = steps.filter((step) => step.id !== "complete");
    const allActionStepsCompleted = actionSteps.every((step) => step.completed);
    const isOnCompletionStep =
      currentStep === steps.length - 1 && steps[currentStep]?.id === "complete";

    if (
      allActionStepsCompleted &&
      isOnCompletionStep &&
      !steps[currentStep]?.completed
    ) {
      setSteps((prevSteps) => {
        const updatedSteps = prevSteps.map((step, index) =>
          index === currentStep ? { ...step, completed: true } : step
        );

        // Immediately save the completion state
        try {
          const stateToSave = {
            version: GUIDE_VERSION,
            steps: updatedSteps,
            currentStep,
            isMinimized,
            dismissed: isDismissed,
            timestamp: Date.now(),
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
        } catch (error) {
          console.error("Error saving completion state:", error);
        }

        return updatedSteps;
      });
    }
  }, [currentStep, steps, isMinimized, isDismissed]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (!isLoading) {
      const stateToSave = {
        version: GUIDE_VERSION,
        steps,
        currentStep,
        isMinimized,
        dismissed: isDismissed,
        timestamp: Date.now(),
      };

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      } catch (error) {
        console.error("Error saving transition guide state:", error);
      }
    }
  }, [steps, currentStep, isMinimized, isDismissed, isLoading]);

  const resetGuide = () => {
    setSteps(TRANSITION_STEPS);
    setCurrentStep(0);
    setIsMinimized(false);
    setIsVisible(true);
    setIsDismissed(false);
  };

  const completeStep = (stepId) => {
    setSteps((prevSteps) => {
      const updatedSteps = prevSteps.map((step) =>
        step.id === stepId ? { ...step, completed: true } : step
      );

      // Use updated steps for next step calculation
      const nextStepIndex = updatedSteps.findIndex(
        (step, index) => index > currentStep && !step.completed
      );

      // Update current step based on updated steps
      if (nextStepIndex !== -1) {
        setCurrentStep(nextStepIndex);
      } else {
        // All action steps completed, move to completion step
        const completionStepIndex = updatedSteps.length - 1;
        setCurrentStep(completionStepIndex);
      }

      // Immediately save to localStorage
      try {
        const finalStepIndex =
          nextStepIndex !== -1 ? nextStepIndex : updatedSteps.length - 1;
        const stateToSave = {
          version: GUIDE_VERSION,
          steps: updatedSteps,
          currentStep: finalStepIndex,
          isMinimized,
          dismissed: isDismissed,
          timestamp: Date.now(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      } catch (error) {
        console.error("Error saving transition guide state:", error);
      }

      return updatedSteps;
    });
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);

      // Immediately save current step change
      try {
        const stateToSave = {
          version: GUIDE_VERSION,
          steps,
          currentStep: stepIndex,
          isMinimized,
          dismissed: isDismissed,
          timestamp: Date.now(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      } catch (error) {
        console.error("Error saving transition guide state:", error);
      }
    }
  };

  const dismissGuide = () => {
    setIsVisible(false);
    setIsDismissed(true);

    // Immediately save dismissal state
    try {
      const stateToSave = {
        version: GUIDE_VERSION,
        steps,
        currentStep,
        isMinimized,
        dismissed: true,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.error("Error saving transition guide state:", error);
    }
  };

  const showGuide = () => {
    setIsVisible(true);
    setIsMinimized(false);
    setIsDismissed(false);
  };

  const toggleMinimized = () => {
    const newMinimizedState = !isMinimized;
    setIsMinimized(newMinimizedState);

    // Immediately save minimized state
    try {
      const stateToSave = {
        version: GUIDE_VERSION,
        steps,
        currentStep,
        isMinimized: newMinimizedState,
        dismissed: isDismissed,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.error("Error saving transition guide state:", error);
    }
  };

  const skipStep = (stepId) => {
    completeStep(stepId); // Same logic as completing for now
  };

  // Calculate progress
  const completedSteps = steps.filter((step) => step.completed).length;
  const totalSteps = steps.length;
  const progressPercent = (completedSteps / totalSteps) * 100;

  // Don't show guide if not authenticated, no pop_au_legacy_id, or still loading

  if (
    auth.authStatus !== "authenticated" ||
    !auth.user?.user?.pop_au_legacy_id ||
    isLoading
  ) {
    return <>{children}</>;
  }

  const value = {
    isVisible,
    isMinimized,
    currentStep,
    steps,
    progressPercent,
    completedSteps,
    totalSteps,
    completeStep,
    goToStep,
    dismissGuide,
    showGuide,
    toggleMinimized,
    skipStep,
    resetGuide,
  };

  return (
    <TransitionGuideContext.Provider value={value}>
      {children}
    </TransitionGuideContext.Provider>
  );
};
